import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, GoldButton } from "@/components/shared";
import { Emblem } from "@/components/Emblem";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success("Welcome back to the crossing.");
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-16 sm:py-24 bg-[#F8F1E7] min-h-[70vh]">
      <Container>
        <div className="max-w-md mx-auto bg-[#FFFAF3] border border-[#D6D3CB] p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4"><Emblem size={56} /></div>
            <h1 className="font-serif text-2xl text-[#003366]">Sign in</h1>
            <p className="font-sans text-sm text-[#505A68] mt-1">Return to your side of the crossing.</p>
          </div>

          {error && (
            <div data-testid="signin-error" className="mb-5 bg-[#003366]/5 border border-[#003366]/30 text-[#003366] text-sm font-sans px-4 py-3">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="signin-form">
            <div>
              <label className={labelCls}>Email</label>
              <input required type="email" data-testid="signin-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input required type="password" data-testid="signin-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            <GoldButton type="submit" disabled={busy} data-testid="signin-submit" className="w-full">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
            </GoldButton>
          </form>

          <p className="text-center font-sans text-sm text-[#505A68] mt-6">
            No account yet? <Link to="/signup" className="text-[#003366] font-semibold link-underline">Create one</Link>
          </p>
        </div>
      </Container>
    </section>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";
